import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import { isValid } from 'date-fns';
/**
 * Convert time (Date or string) to minutes from midnight (0-1439)
 */
function timeToMinutes(time) {
    if (!time)
        return 0;
    try {
        const date = typeof time === 'string' ? new Date(time) : time;
        if (!isValid(date))
            return 0;
        return date.getHours() * 60 + date.getMinutes();
    }
    catch (_a) {
        return 0;
    }
}
/**
 * Convert minutes from midnight to Date object (today's date with that time)
 */
function minutesToDate(minutes) {
    const date = new Date();
    date.setHours(Math.floor(minutes / 60));
    date.setMinutes(minutes % 60);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}
/**
 * Format minutes as HH:mm
 */
function formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
/**
 * @export
 * TimeRangeSliderPicker - A dual-handle slider for selecting time ranges
 *
 * @example
 * ```tsx
 * <TimeRangeSliderPicker
 *     title="Working Hours"
 *     startTime="09:00"
 *     endTime="17:00"
 *     onChange={(start, end) => {
 *         console.log('Start:', start, 'End:', end);
 *     }}
 *     step={15}
 * />
 * ```
 */
const TimeRangeSliderPicker = (props) => {
    const context = React.useContext(UXPContext);
    const { title, startTime, endTime, onChange, step = 15, disabled = false } = props;
    // State: minutes from midnight
    const [startMinutes, setStartMinutes] = useState(0);
    const [endMinutes, setEndMinutes] = useState(1439);
    const [isDragging, setIsDragging] = useState(false);
    const [activeHandle, setActiveHandle] = useState(null);
    // Sync props to state
    useEffect(() => {
        const start = timeToMinutes(startTime);
        const end = timeToMinutes(endTime);
        // Ensure end >= start
        if (end < start) {
            setStartMinutes(end);
            setEndMinutes(start);
        }
        else {
            setStartMinutes(start);
            setEndMinutes(end);
        }
    }, [startTime, endTime]);
    // Calculate range percentage for visual highlight
    const rangeStyle = useMemo(() => {
        const startPercent = (startMinutes / 1439) * 100;
        const endPercent = (endMinutes / 1439) * 100;
        return {
            left: `${startPercent}%`,
            width: `${endPercent - startPercent}%`
        };
    }, [startMinutes, endMinutes]);
    // Handle start time change
    const handleStartChange = useCallback((e) => {
        const newStart = parseInt(e.target.value, 10);
        // Don't allow start to exceed end
        if (newStart <= endMinutes) {
            setStartMinutes(newStart);
            onChange(minutesToDate(newStart), minutesToDate(endMinutes));
        }
    }, [endMinutes, onChange]);
    // Handle end time change
    const handleEndChange = useCallback((e) => {
        const newEnd = parseInt(e.target.value, 10);
        // Don't allow end to be less than start
        if (newEnd >= startMinutes) {
            setEndMinutes(newEnd);
            onChange(minutesToDate(startMinutes), minutesToDate(newEnd));
        }
    }, [startMinutes, onChange]);
    // Time labels along the slider
    const timeLabels = ['00:00', '06:00', '12:00', '18:00', '24:00'];
    return (_jsxs("div", { className: classNames('uxpcore_timerangeslider', {
            'uxpcore_timerangeslider--disabled': disabled,
            'uxpcore_timerangeslider--dragging': isDragging
        }), children: [title && _jsx("div", { className: "uxpcore_timerangeslider__title", children: title }), _jsxs("div", { className: "uxpcore_timerangeslider__values", children: [_jsxs("div", { className: "uxpcore_timerangeslider__value uxpcore_timerangeslider__value--start", children: [_jsxs("span", { className: "uxpcore_timerangeslider__value-label", children: [context.$L('uxp-core.text.from'), ":"] }), _jsx("span", { className: "uxpcore_timerangeslider__value-time", children: formatTime(startMinutes) })] }), _jsxs("div", { className: "uxpcore_timerangeslider__value uxpcore_timerangeslider__value--end", children: [_jsxs("span", { className: "uxpcore_timerangeslider__value-label", children: [context.$L('uxp-core.text.to'), ":"] }), _jsx("span", { className: "uxpcore_timerangeslider__value-time", children: formatTime(endMinutes) })] })] }), _jsxs("div", { className: "uxpcore_timerangeslider__slider", children: [_jsx("div", { className: "uxpcore_timerangeslider__track", children: _jsx("div", { className: "uxpcore_timerangeslider__range", style: rangeStyle }) }), _jsx("input", { type: "range", className: classNames('uxpcore_timerangeslider__input', 'uxpcore_timerangeslider__input--start', { 'is-active': activeHandle === 'start' }), min: "0", max: "1439", step: step, value: startMinutes, onChange: handleStartChange, onMouseDown: () => {
                            setIsDragging(true);
                            setActiveHandle('start');
                        }, onMouseUp: () => {
                            setIsDragging(false);
                            setActiveHandle(null);
                        }, onTouchStart: () => {
                            setIsDragging(true);
                            setActiveHandle('start');
                        }, onTouchEnd: () => {
                            setIsDragging(false);
                            setActiveHandle(null);
                        }, disabled: disabled, "aria-label": "Start time" }), _jsx("input", { type: "range", className: classNames('uxpcore_timerangeslider__input', 'uxpcore_timerangeslider__input--end', { 'is-active': activeHandle === 'end' }), min: "0", max: "1439", step: step, value: endMinutes, onChange: handleEndChange, onMouseDown: () => {
                            setIsDragging(true);
                            setActiveHandle('end');
                        }, onMouseUp: () => {
                            setIsDragging(false);
                            setActiveHandle(null);
                        }, onTouchStart: () => {
                            setIsDragging(true);
                            setActiveHandle('end');
                        }, onTouchEnd: () => {
                            setIsDragging(false);
                            setActiveHandle(null);
                        }, disabled: disabled, "aria-label": "End time" })] }), _jsx("div", { className: "uxpcore_timerangeslider__labels", children: timeLabels.map((label, index) => (_jsx("div", { className: "uxpcore_timerangeslider__label", children: label }, index))) })] }));
};
export default TimeRangeSliderPicker;
